import React, { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  FormInputArtifact,
  FormField,
  CredentialConflictCandidate,
  CredentialConflictsResponse,
} from '@dreamai/types';
import api from '../../../api';
import { ReactComponent as NoLongerNeededIcon } from '../../../icons/no-longer-needed.svg';
import SecureModal from './SecureModal';
import CredentialFieldEditor, {
  CredentialRow,
  collapseRowsForSubmit,
  hasAtLeastOneSecretValue,
  isSecretFieldName,
  makeCredentialRow,
  rowIncompletenessByRowId,
  rowsHaveDuplicateNames,
} from './CredentialFieldEditor';
import './secure-credential-modal.scss';

/**
 * Submit options forwarded alongside the credential field values. Used
 * by callers that POST to /memories/store-credential or
 * /response-artifacts/.../submit and need to pass the disambiguator,
 * the explicit secret-key set, or the replace-target along to the BE.
 *
 * Keep additive — every new field is optional so existing callers stay
 * compatible without touching their handlers.
 */
export interface SecureCredentialSubmitOptions {
  /** Free-text disambiguator for users with multiple credentials per site. */
  purpose?: string;
  /**
   * Echo of the update-mode anchor so the BE can deactivate the old row.
   * Mirrors the prop the caller passed in as `existingCredentialId`.
   */
  replacesCredentialId?: string;
  /**
   * Explicit list of field keys whose values are secrets. Forwarded to the
   * BE secret-count gate so user-chosen field names (e.g. "magic_word")
   * are honoured rather than rejected by the key-name heuristic.
   */
  secretFieldKeys?: string[];
  /**
   * User-chosen label, when the modal exposed the Label input itself
   * (i.e. the caller didn't pass a fixed `label` prop). The caller forwards
   * this to the BE in place of any auto-derived label.
   */
  label?: string;
  /**
   * User-chosen site, when the modal exposed the Site input itself
   * (the user-initiated Memories Add path).
   */
  site?: string;
}

interface SecureCredentialModalProps {
  /**
   * Optional response-artifact source. When supplied, the modal pre-seeds
   * rows from `artifact.fields`, locks their names, and resolves
   * label/site/overview/purpose/existingCredentialId from the artifact
   * unless explicit props override them.
   */
  artifact?: FormInputArtifact;
  /**
   * Modal title rendered above the body. Falls back to the artifact's
   * title and then to a sensible default.
   */
  title?: string;
  /**
   * When supplied, the credential's `site` is fixed (renders as a badge,
   * not an editable input). When omitted AND the modal isn't in artifact
   * mode, the user can type a site themselves.
   */
  site?: string;
  /**
   * Optional fixed label. When supplied, the modal does NOT render the
   * Label input — the credential's row-level label is decided by the
   * caller (e.g. the artifact title, or the agent's clarifying question).
   * When omitted (user-initiated Memories Add), the Label input shows
   * up so the user can name the credential themselves.
   */
  label?: string;
  /**
   * Body copy rendered above the editor. Same precedence as `title`.
   */
  overview?: string;
  /**
   * @deprecated Use `initialRows` for new code. Legacy callers pass a
   * `FormField[]` and the modal converts each entry to a locked editor
   * row (matching the old rigid-field behaviour). Prefer `initialRows`
   * so the caller controls lock state and helper visibility directly.
   */
  formFields?: FormField[];
  /**
   * Pre-seeded editor rows. When neither this nor `formFields` nor an
   * artifact is supplied, the editor defaults to a `[username, password]`
   * pair (the common-case user-initiated shape).
   */
  initialRows?: CredentialRow[];
  /**
   * Names that can't be renamed or removed in the editor. Defaults to the
   * names derived from `artifact.fields` or `formFields` (agent-supplied
   * shapes are locked); user-initiated paths leave this empty so every
   * row can be edited freely.
   */
  lockedKeys?: string[];
  submitLabel?: string;
  /**
   * Mark the modal as REPLACING an existing saved credential. Shown as
   * an update banner; on submit the id is echoed back to the caller as
   * `opts.replacesCredentialId` so it can hit the right BE flag.
   */
  existingCredentialId?: string;
  /**
   * Whether the user can add rows beyond the initial seed. Defaults to
   * true everywhere — the data model is "arbitrary fields, at least one
   * secret" so the user should always be free to extend a credential.
   */
  allowAdd?: boolean;
  /**
   * Helper buttons to expose on rows. Only the ops worker form passes
   * `useUserEmail`; password generation and copy default to off.
   */
  helpers?: {
    generatePassword?: boolean;
    useUserEmail?: () => Promise<string | null>;
    copyValue?: boolean;
  };
  /**
   * Initial value for the in-modal Label input. Used by edit-existing
   * callers (CredentialCard) to pre-fill the input with the current row
   * label so the user can rename inline.
   */
  initialLabel?: string;
  /**
   * Initial value for the in-modal Site input. Same role as initialLabel
   * — pre-fills the input when entering update mode so the user sees
   * what's currently stored.
   */
  initialSite?: string;
  onSubmit: (
    values: Record<string, string>,
    opts?: SecureCredentialSubmitOptions,
  ) => void;
  onAcknowledge?: () => void;
  onClose: () => void;
  submitting: boolean;
  readOnly?: boolean;
}

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

const DEFAULT_INITIAL_ROWS = (): CredentialRow[] => [
  makeCredentialRow({
    name: 'Username',
    value: '',
    isSecret: false,
  }),
  makeCredentialRow({
    name: 'Password',
    value: '',
    isSecret: true,
  }),
];

/**
 * Convert a legacy FormField list into the editor's row shape. Each
 * field becomes a locked-name row so existing callers that pass
 * `formFields` continue to get the rigid behaviour they expect.
 */
function rowsFromFormFields(fields: FormField[]): CredentialRow[] {
  return fields.map((f) =>
    makeCredentialRow({
      name: capitalize(f.fieldId),
      value: '',
      isSecret: isSecretFieldName(f.fieldId),
      placeholder: f.placeholder ?? undefined,
      displayLabel: f.label,
    }),
  );
}

const SecureCredentialModal: React.FC<SecureCredentialModalProps> = ({
  artifact,
  title: titleProp,
  site: siteProp,
  label: labelProp,
  overview: overviewProp,
  formFields,
  initialRows,
  lockedKeys: lockedKeysProp,
  submitLabel: submitLabelProp,
  existingCredentialId: existingCredentialIdProp,
  allowAdd = true,
  helpers,
  initialLabel,
  initialSite,
  onSubmit,
  onAcknowledge,
  onClose,
  submitting,
  readOnly,
}) => {
  // ─── Resolve top-level metadata ────────────────────────────────────────
  const artifactTitle = titleProp || artifact?.title || 'Save sign-in details';
  const fixedSite = siteProp || artifact?.credentialSite || undefined;
  const fixedLabel = labelProp || artifact?.title || undefined;
  const overview = overviewProp || artifact?.overview;
  const existingCredentialId =
    existingCredentialIdProp || artifact?.existingCredentialId;
  const isUpdate = !!existingCredentialId;
  const submitLabel =
    submitLabelProp || artifact?.submitLabel || (isUpdate ? 'Update' : 'Save');
  const headerTitle = isUpdate
    ? 'Update sign-in details'
    : 'Save sign-in details';

  // Show the label/site inputs:
  //  - when the caller didn't fix them (user-initiated paths)
  //  - OR when we're in update mode (the user is editing the row, so
  //    even an agent-supplied label should be renameable; same for site)
  // TODO: re-enable once label UX is clearer for users
  const showLabelInput = false; // !fixedLabel || isUpdate;
  const showSiteInput = !fixedSite || isUpdate;

  // ─── Editor rows ────────────────────────────────────────────────────────
  // Resolve initial rows in order of precedence: explicit `initialRows`,
  // legacy `formFields`, artifact's `fields`, then the default seed.
  // We also track whether the caller hand-supplied a row shape so the
  // auto-reveal effect below knows whether it should fetch the live
  // credential fields (update-mode callers like the "Yes, change" flow
  // pass `existingCredentialId` but no rows — without an auto-fetch the
  // editor would render blank `username`/`password` rows instead of the
  // user's actual stored values).
  // Memoise on mount only — same reasoning as initialRowsResolved
  // (we don't want a new prop reference to flip this back to false
  // mid-edit and trigger a re-fetch that clobbers the user's typing).
  const callerProvidedRows = useMemo(
    () =>
      (initialRows && initialRows.length > 0) ||
      (formFields && formFields.length > 0) ||
      !!(artifact?.fields && artifact.fields.length > 0),
    [],
  );
  // Seeds are deliberately memoised on mount only — we don't want the
  // user's typing to be reset if the caller hands back a new formFields
  // array reference on every render.
  const initialRowsResolved: CredentialRow[] = useMemo(() => {
    if (initialRows && initialRows.length > 0) return initialRows;
    if (formFields && formFields.length > 0)
      return rowsFromFormFields(formFields);
    if (artifact?.fields && artifact.fields.length > 0)
      return rowsFromFormFields(artifact.fields);
    return DEFAULT_INITIAL_ROWS();
  }, []);

  const [rows, setRows] = useState<CredentialRow[]>(initialRowsResolved);

  // Locked keys default to the agent-supplied shape (artifact / formFields)
  // unless the caller overrides. The default rows (Username, Password) are
  // also locked so the user can only fill values, not rename them.
  const lockedKeys: string[] = useMemo(() => {
    if (lockedKeysProp) return lockedKeysProp;
    if (formFields && formFields.length > 0)
      return formFields.map((f) => capitalize(f.fieldId));
    if (artifact?.fields && artifact.fields.length > 0)
      return artifact.fields.map((f) => capitalize(f.fieldId));
    return ['Username', 'Password'];
  }, [lockedKeysProp, formFields, artifact?.fields]);

  // ─── Label / Site / Purpose top-level inputs ───────────────────────────
  // In update mode we pre-fill from `initialLabel`/`initialSite` (the row's
  // current values) so the user sees what's stored and can change either.
  // If the caller didn't pass an explicit `initialLabel` but we're in update
  // mode, fall back to `labelProp`/`artifact.title` — every update-mode
  // caller passes one of those (it's what the conflict banner / heading
  // shows), and falling back lets the input pre-populate without forcing
  // every caller to wire `initialLabel` separately.
  const [userLabel, setUserLabel] = useState<string>(
    initialLabel ?? (isUpdate ? (fixedLabel ?? '') : ''),
  );
  const [userSite, setUserSite] = useState<string>(
    initialSite || fixedSite || '',
  );

  const { getAccessTokenSilently } = useAuth0();

  // ─── Auto-reveal in update mode ────────────────────────────────────────
  // When a caller passes `existingCredentialId` but no rows seed (the
  // common "Yes, change" / TaskEscalation / AddTodo flow), fetch the
  // current vault fields so the editor shows what's actually stored.
  // Without this the modal would render the default `[username, password]`
  // skeleton even when the saved credential has e.g. `email`, `password`
  // and `notes` rows — confusing the user into thinking their data was
  // lost. Skip the fetch when the caller hand-supplied row data
  // (CredentialCard does this; it has already revealed the fields and
  // wants those exact values pre-populated).
  const [revealing, setRevealing] = useState<boolean>(
    !callerProvidedRows && !!existingCredentialId,
  );
  const [revealError, setRevealError] = useState<string | null>(null);

  useEffect(() => {
    if (callerProvidedRows || !existingCredentialId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<{
          success: boolean;
          fields?: Record<string, string>;
          error?: string;
        }>(`/memories/credentials/${existingCredentialId}/reveal`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const fields = response.data?.fields;
        if (!fields) {
          setRevealError(
            response.data?.error || 'Failed to load saved credential',
          );
          setRevealing(false);
          return;
        }
        const seeded = Object.entries(fields).map(([k, v]) =>
          makeCredentialRow({
            name: k,
            value: v,
            isSecret: isSecretFieldName(k),
          }),
        );
        if (seeded.length > 0) setRows(seeded);
        setRevealing(false);
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to load saved credential';
        setRevealError(message);
        setRevealing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Mount-only on purpose: re-fetching after the user has already
    // started editing would clobber their in-progress changes. The
    // caller can re-mount the modal to force a refresh.
  }, []);

  // ─── Conflict pre-flight ───────────────────────────────────────────────
  // The conflict pre-flight populates `conflicts` with sibling credentials
  // that share the same site. When the list is non-empty we escalate the
  // single `Label` input below — required marker, hint copy shifts, submit
  // gates on it being non-empty. We deliberately do NOT render a second
  // "Label to tell this sign-in apart" field: from the user's perspective
  // those two inputs answer the same question ("what should I call this?")
  // and having two of them on the same form is confusing. The disambiguation
  // case is just the required-variant of the optional case.
  const [conflicts, setConflicts] = useState<CredentialConflictCandidate[]>([]);
  const effectiveSite = (fixedSite || userSite || '').trim();

  useEffect(() => {
    if (isUpdate || !effectiveSite) {
      setConflicts([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<CredentialConflictsResponse>(
          '/memories/credential-conflicts',
          {
            params: { site: effectiveSite, credentialType: 'login' },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!cancelled) setConflicts(response.data?.candidates || []);
      } catch (err) {
        console.warn('[SecureCredentialModal] Conflict pre-flight failed', err);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [effectiveSite, isUpdate, getAccessTokenSilently]);

  // ─── Submit gating ─────────────────────────────────────────────────────
  // Three hard rules:
  //  1. At least one row marked Secret must carry a non-empty value
  //     (mirrors the BE secret-count gate).
  //  2. No duplicate field names (the BE would last-write-wins them, but
  //     it almost always indicates a user mistake).
  //  3. Every row carrying a value must also have a name. Without this,
  //     `collapseRowsForSubmit` silently drops the row on serialisation
  //     and the user thinks the value was saved when it wasn't.
  // The label input is genuinely optional everywhere — the caller / BE
  // derives a label from site + username + email when the user leaves it
  // blank, so we don't gate the submit on it even though we display it
  // prominently.
  const hasSecret = hasAtLeastOneSecretValue(rows);
  const hasDuplicates = rowsHaveDuplicateNames(rows).size > 0;
  // Block submit when any row is incomplete (missing name OR missing
  // value). The user can always remove fields they don't intend to
  // populate via the × button. This stops:
  //   - silent data loss when a value is typed into a nameless row
  //   - empty-value rows cluttering the vault
  //   - half-filled rows from the user clicking "+ Add field" then
  //     forgetting to fill it before saving
  const hasIncompleteRows = rowIncompletenessByRowId(rows).size > 0;
  // Conflicts → label becomes required. We can't auto-derive a unique label
  // for the user without guessing; better to make them pick one.
  const labelRequired = !isUpdate && conflicts.length > 0 && showLabelInput;
  const labelMissing = labelRequired && userLabel.trim().length === 0;
  const submitOk =
    hasSecret &&
    !hasDuplicates &&
    !hasIncompleteRows &&
    !labelMissing &&
    !revealing;

  // ─── Generate-password helper (opt-in via helpers.generatePassword) ────
  const generatePassword = async (): Promise<string | null> => {
    if (!helpers?.generatePassword) return null;
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/memories/generate-password',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data?.password ?? null;
    } catch (err) {
      console.warn('[SecureCredentialModal] Generate password failed', err);
      return null;
    }
  };

  const editorHelpers = useMemo(
    () => ({
      generatePassword: helpers?.generatePassword
        ? generatePassword
        : undefined,
      useUserEmail: helpers?.useUserEmail,
      copyValue: helpers?.copyValue,
    }),
    // generatePassword is recreated each render but the editor only
    // captures it during the click handler — stable identity isn't
    // necessary, so we intentionally omit it from the dep list.
    [helpers?.generatePassword, helpers?.useUserEmail, helpers?.copyValue],
  );

  const handleSubmit = () => {
    if (!submitOk || submitting || readOnly) return;
    const { fields, secretFieldKeys } = collapseRowsForSubmit(rows);
    // Label/site forwarding rules:
    //  - Create mode: send only when the user typed something. Empty stays
    //    empty so the caller's fallback derivation kicks in (e.g.
    //    Memories Add derives a label from the site / username).
    //  - Update mode: ALWAYS send the current value (even when empty so
    //    the caller can clear the site column via PATCH). Without this
    //    the user couldn't blank out a previously-set site.
    const labelOpt = showLabelInput
      ? isUpdate
        ? { label: userLabel.trim() }
        : userLabel.trim()
          ? { label: userLabel.trim() }
          : {}
      : {};
    const siteOpt = showSiteInput
      ? isUpdate
        ? { site: userSite.trim() }
        : userSite.trim()
          ? { site: userSite.trim() }
          : {}
      : {};
    onSubmit(fields, {
      // No separate `purpose` payload — the FE now exposes a single
      // `Label` input that handles both the "what to call this" and
      // "how to disambiguate from siblings" cases. The BE column for
      // `purpose` still exists for legacy data; new entries only set
      // `label`.
      replacesCredentialId: existingCredentialId,
      secretFieldKeys,
      ...labelOpt,
      ...siteOpt,
    });
  };

  const acknowledgeButton = onAcknowledge ? (
    <button
      className='no-longer-needed-btn'
      onClick={onAcknowledge}
      disabled={submitting}
    >
      <NoLongerNeededIcon /> Remove task
    </button>
  ) : undefined;

  return (
    <SecureModal
      show
      onClose={onClose}
      title={headerTitle}
      shieldText="These details are saved securely. They're never stored on your device or made available to the assistant."
      submitLabel={submitLabel}
      submittingLabel={isUpdate ? 'Updating…' : 'Saving…'}
      onSubmit={handleSubmit}
      submitDisabled={!submitOk || readOnly}
      submitting={submitting}
      footerLeft={acknowledgeButton}
      className='secure-credential-modal'
    >
      <h2 className='artifact-title'>{artifactTitle}</h2>

      {isUpdate && (
        <div className='sc-update-banner'>
          You&rsquo;re replacing your saved sign-in
          {fixedSite ? (
            <>
              {' '}
              for <strong>{fixedSite}</strong>
            </>
          ) : null}
          .
        </div>
      )}

      {!isUpdate && fixedSite && (
        <div className='sc-site-badge'>
          Sign-in for <strong>{fixedSite}</strong>
        </div>
      )}

      {overview && <div className='artifact-overview'>{overview}</div>}

      {conflicts.length > 0 && (
        <div className='sc-conflict-banner'>
          <div className='sc-conflict-title'>
            You already have {conflicts.length} saved sign-in
            {conflicts.length > 1 ? 's' : ''} for this service.
          </div>
          <ul className='sc-conflict-list'>
            {conflicts.map((c) => (
              <li key={c.credentialId}>
                <strong>{c.label}</strong>
                {c.purpose ? <span> — {c.purpose}</span> : null}
              </li>
            ))}
          </ul>
          <div className='sc-conflict-hint'>
            If this is the <em>same</em> account, close this and edit the
            existing entry in your Memories instead. If it&rsquo;s a different
            account, give it a label below so you can tell them apart.
          </div>
        </div>
      )}

      {showSiteInput && (
        <div className='sc-top-field'>
          <label className='sc-top-field-label'>Website or service</label>
          <input
            className='sc-top-field-input'
            type='text'
            placeholder='e.g. ebay.com'
            value={userSite}
            onChange={(e) => setUserSite(e.target.value)}
            disabled={readOnly || submitting}
            spellCheck={false}
            autoCapitalize='none'
          />
        </div>
      )}

      {showLabelInput && (
        <div className='sc-top-field'>
          <label className='sc-top-field-label'>
            {labelRequired
              ? 'Label — give this sign-in a name to tell it apart'
              : 'Label (optional)'}
            {labelRequired && <span className='required-indicator'>*</span>}
          </label>
          <input
            className={`sc-top-field-input ${labelMissing ? 'sc-top-field-input--error' : ''}`}
            type='text'
            placeholder={
              labelRequired
                ? 'e.g. Personal account, Work expenses, Charlie\u2019s admin login'
                : 'Describe the account (e.g. "Amazon Business Login")'
            }
            value={userLabel}
            onChange={(e) => setUserLabel(e.target.value)}
            disabled={readOnly || submitting}
            maxLength={120}
          />
        </div>
      )}

      {revealing ? (
        <div className='sc-revealing-hint'>
          Loading your saved sign-in details…
        </div>
      ) : (
        <CredentialFieldEditor
          rows={rows}
          onChange={setRows}
          lockedKeys={lockedKeys}
          disabled={readOnly || submitting}
          helpers={editorHelpers}
          allowAdd={allowAdd}
        />
      )}

      {revealError && (
        <div className='sc-reveal-error'>
          Couldn&rsquo;t load your saved sign-in: {revealError}. You can still
          re-enter the details below — saving will overwrite the existing entry.
        </div>
      )}
    </SecureModal>
  );
};

export default SecureCredentialModal;
